import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Button, message, Radio, Space, Alert } from 'antd';
import { DollarOutlined, CreditCardOutlined, WalletOutlined } from '@ant-design/icons';
import api from '../services/api';

interface RechargeModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface PaymentConfig {
  enabled: boolean;
  apiUrl: string;
}

const RechargeModal: React.FC<RechargeModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'balance' | 'rainbow_pay'>('rainbow_pay');

  useEffect(() => {
    if (visible) {
      loadPaymentConfig();
    }
  }, [visible]);

  const loadPaymentConfig = async () => {
    try {
      const response = await api.get('/payment/rainbow/config');
      setPaymentConfig(response.data);
    } catch (error) {
      console.error('获取支付配置失败:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (paymentMethod === 'rainbow_pay') {
        // 使用彩虹易支付
        const response = await api.post('/payment/rainbow/create-order', {
          amount: values.amount,
          type: 'balance_recharge',
          description: '账户余额充值',
          return_url: `${window.location.origin}/payment/notify`
        });

        if (response.data.success) {
          // 跳转到支付页面
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = response.data.payUrl;
          form.target = '_blank';

          Object.keys(response.data.payParams).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = response.data.payParams[key];
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);

          message.success('正在跳转到支付页面...');
          onCancel();
        } else {
          message.error(response.data.message || '创建支付订单失败');
        }
      } else {
        // 使用余额支付（这里可以添加其他支付方式）
        message.info('余额支付功能暂未开放');
      }
    } catch (error: any) {
      console.error('充值失败:', error);
      message.error(error.response?.data?.message || '充值失败');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [10, 20, 50, 100, 200, 500];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DollarOutlined style={{ color: '#52c41a' }} />
          账户充值
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ amount: 10 }}
      >
        <Form.Item
          label="充值金额"
          name="amount"
          rules={[
            { required: true, message: '请输入充值金额' },
            { type: 'number', min: 1, message: '充值金额必须大于0' },
            { type: 'number', max: 10000, message: '单次充值金额不能超过10000元' }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="请输入充值金额"
            prefix="¥"
            precision={2}
            min={1}
            max={10000}
          />
        </Form.Item>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '8px', fontWeight: 500 }}>快速选择金额：</div>
          <Space wrap>
            {quickAmounts.map(amount => (
              <Button
                key={amount}
                size="small"
                onClick={() => form.setFieldsValue({ amount })}
              >
                ¥{amount}
              </Button>
            ))}
          </Space>
        </div>

        <Form.Item
          label="支付方式"
          name="paymentMethod"
          initialValue="rainbow_pay"
        >
          <Radio.Group
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value="rainbow_pay" disabled={!paymentConfig?.enabled}>
                <Space>
                  <CreditCardOutlined />
                  彩虹易支付
                  {!paymentConfig?.enabled && <span style={{ color: '#999' }}>(未启用)</span>}
                </Space>
              </Radio>
              <Radio value="balance" disabled>
                <Space>
                  <WalletOutlined />
                  余额支付
                  <span style={{ color: '#999' }}>(暂未开放)</span>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {paymentMethod === 'rainbow_pay' && paymentConfig?.enabled && (
          <Alert
            message="支付说明"
            description="点击确认后将跳转到彩虹易支付页面完成支付，支付成功后余额将自动到账。"
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              取消
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={paymentMethod === 'rainbow_pay' && !paymentConfig?.enabled}
              icon={<DollarOutlined />}
            >
              确认充值
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RechargeModal;
